<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useToast } from "vue-toast-notification";

import { useDefaultToast } from "@/composables/useDefaultToast";
import { ModalName } from '@/constants/modals';
import { ROUTES } from '@/constants/routes';
import { toCalendarDayUtcIso } from "@/helpers/date";
import { DOCUMENT_EXTENSIONS_ALLOWED } from "@/helpers/file";
import { resolveDuplicateEmailWorkerId } from "@/modules/worker/helpers/duplicateWorker";
import workerService from "@/modules/worker/services/workerService";
import { useWorkerAddIdentificationStore } from "@/modules/worker/store/workerAddIdentificationStore";
import { useRegistrationsStore } from "@/store/registrationsStore";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import CheckboxLabel from "@/dsComponents/checkbox/CheckboxLabel.vue";
import Loader from "@/dsComponents/Loader.vue";
import Modal from "@/dsComponents/modals/Modal.vue";
import { useModalStore } from "@/dsComponents/modals/modalStore";

import PdfManager from "@/components/PdfManager.vue";
import CreateCandidateForm from "@/modules/worker/components/create/CreateCandidateForm.vue";

interface CreateCandidateModalProps {
    open: boolean;
    companyNeedId?: string;
}

const props = defineProps<CreateCandidateModalProps>();

const emit = defineEmits<{
    close: [];
}>();

const { t } = useI18n();
const router = useRouter();
const toaster = useToast();
const { toastError } = useDefaultToast();
const modalStore = useModalStore();
const workerAddIdentificationStore = useWorkerAddIdentificationStore();
const registrationsStore = useRegistrationsStore();

workerAddIdentificationStore.reset();

const isCreateCandidateLoading = ref(false);

const isLoading = computed(() => {
    return workerAddIdentificationStore.loading || isCreateCandidateLoading.value;
});

const isFormCompleted = computed(() => {
    const { findWorkerForm } = workerAddIdentificationStore;

    const isFirstNameValid = findWorkerForm.firstname.value && findWorkerForm.firstname.isValid;
    const isLastNameValid = findWorkerForm.lastname.value && findWorkerForm.lastname.isValid;
    const isPhoneValid = findWorkerForm.phone.value && findWorkerForm.phone.isValid;
    const isEmailValid = findWorkerForm.email.value && findWorkerForm.email.isValid;
    const isOriginSet = !!findWorkerForm.origin.value;
    const hasJobs = !!findWorkerForm.jobs.value.length;
    const isBirthDateValid = findWorkerForm.birthDate.value ? findWorkerForm.birthDate.isValid : true;

    return isFirstNameValid && isLastNameValid && isPhoneValid && isOriginSet && hasJobs && isEmailValid && isBirthDateValid;
});

const handleDropCv = async (cv: string) => {
    try {
        await workerAddIdentificationStore.fetchCvParsing(cv);
    } catch {
        toaster.open({
            message: t("page.worker.modal.create.errors.cvParsingFail"),
            type: "warning",
            duration: 6000,
        });
    }
};

const createNewCandidate = async () => {
    try {
        isCreateCandidateLoading.value = true;

        const { findWorkerForm, cv } = workerAddIdentificationStore;
        const payload = {
            firstname: findWorkerForm.firstname.value,
            lastname: findWorkerForm.lastname.value,
            email: findWorkerForm.email.value,
            phone: findWorkerForm.phone.value,
            birthDate: toCalendarDayUtcIso(findWorkerForm.birthDate.value),
            cv: { path: cv },
            source: findWorkerForm.origin.value?.value,
            jobs: findWorkerForm.jobs.value?.map((job) => job.value) ?? [],
            besoinId: props.companyNeedId,
            addToRegistrationsTracking: workerAddIdentificationStore.addToRegistrationsTracking,
        };

        const result = await workerService.createWorker(payload, {
            jobsFromForm: findWorkerForm.jobs.value,
        });

        if (payload.addToRegistrationsTracking) registrationsStore.incrementCount();

        emit("close");

        router.push({ name: ROUTES.APP.WORKERS.PROFIL._ROOT, params: { workerId: result.id } });
    } catch (error: unknown) {
        const existingWorkerId = await resolveDuplicateEmailWorkerId(error, workerAddIdentificationStore.findWorkerForm.email.value);
        if (existingWorkerId) {
            workerAddIdentificationStore.setDuplicatedWorkerFromForm(existingWorkerId);
            modalStore.actions.open(ModalName.DuplicatedWorkerModal, { companyNeedId: props.companyNeedId });
            modalStore.actions.close(ModalName.CreateCandidateModal);
            return;
        }

        let errorMessage = t("page.worker.modal.create.errors.default");

        const errorResponse = (error as { response: { status: number; data: { message: string } } }).response;
        if (errorResponse.data?.message) errorMessage = errorResponse.data.message;

        toaster.error(errorMessage);
    } finally {
        isCreateCandidateLoading.value = false;
    }
};

const handleCheckDuplicatedWorkers = async () => {
    try {
        const result = await workerAddIdentificationStore.fetchDuplicatedWorkers();

        if (result === null) {
            createNewCandidate();
        } else if (result.action === "AUTO_MERGE") {
            router.push({ name: ROUTES.APP.WORKERS.PROFIL._ROOT, params: { workerId: result.id } });
            modalStore.actions.close(ModalName.CreateCandidateModal);
        } else {
            modalStore.actions.open(ModalName.DuplicatedWorkerModal, {
                companyNeedId: props.companyNeedId,
            });
            modalStore.actions.close(ModalName.CreateCandidateModal);
        }
    } catch (error: unknown) {
        const typedError = error as { response: { status: number; data: { error: string; locale: string } } };

        if (typedError.response.status === 401) {
            toastError(t("errors.UserContractManager.EvoliaConnexionFailed"));
        } else {
            toastError(t("page.worker.modal.create.errors.default"));
        }
    }
};

const handleClose = () => {
    emit("close");
};
</script>
<template>
    <Modal
        class="create-candidate-modal"
        :title="t('page.worker.modal.create.title.createProfile')"
        width="900px"
        :open="open"
        close
        @close="handleClose"
    >
        <div class="create-candidate-modal__form">
            <div class="create-candidate-modal__form-document">
                <div class="create-candidate-modal__form-title typo-md-medium">
                    {{ t("page.worker.modal.create.form.title.cv") }}
                </div>

                <div class="create-candidate-modal__form-document-manager">
                    <PdfManager path="" show-rules :extension-accept="DOCUMENT_EXTENSIONS_ALLOWED" @add="handleDropCv" />
                    <div v-if="isLoading" class="create-candidate-modal__form-document-manager--loading">
                        <Loader />
                    </div>
                </div>
            </div>

            <div class="create-candidate-modal__form-infos">
                <div class="create-candidate-modal__form-title typo-md-medium">
                    {{ t("page.worker.modal.create.form.title.personalInformations") }}
                </div>
                <CreateCandidateForm />
                <CheckboxLabel
                    :label="t('page.worker.modal.create.form.label.registrationsTracking')"
                    :checked="workerAddIdentificationStore.addToRegistrationsTracking"
                    size="sm"
                    @input="workerAddIdentificationStore.addToRegistrationsTracking = !workerAddIdentificationStore.addToRegistrationsTracking"
                />
            </div>
        </div>

        <template #footer>
            <div class="create-candidate-modal__footer-buttons">
                <ButtonAlt type="neutral" :label="t('global.actions.cancel')" @click="handleClose" />
                <ButtonAlt
                    variant="filled"
                    :label="t('global.actions.validate')"
                    :disabled="!isFormCompleted"
                    :loading="isLoading"
                    @click="handleCheckDuplicatedWorkers"
                />
            </div>
        </template>
    </Modal>
</template>

<style scoped lang="scss">
.create-candidate-modal {
    &__form {
        display: flex;
        align-items: flex-start;
        gap: var(--spacing-3xl, 24px);

        &-document {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            flex-shrink: 0;
            gap: var(--spacing-lg, 12px);
            width: 460px;

            &-manager {
                width: 100%;
                height: 380px;
                position: relative;

                &--loading {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    z-index: 12;
                    border-radius: var(--radius-md, 8px);
                    background-color: rgba(#ffffff, 0.8);
                }
            }
        }

        &-infos {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            flex: 1;
            gap: var(--spacing-lg, 12px);
            min-width: 0;
        }
    }

    &__footer {
        &-buttons {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: var(--spacing-lg, 12px);
        }
    }
}
</style>
